import {NetworkStatus, useMutation, useQuery} from '@apollo/client'
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Divider,
  Grid,
  InputAdornment,
  LinearProgress,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material'
import {makeStyles} from '@mui/styles'
import {addYears, isToday} from 'date-fns'
import gql from 'graphql-tag'
import {isFinite, isNumber, pick, round} from 'lodash-es'
import {FC, useCallback, useContext, useEffect, useMemo, useState} from 'react'
import {
  AutocompleteInput,
  BooleanField,
  Create,
  Datagrid,
  DateField,
  EditContextProvider,
  FilterLiveSearch,
  FunctionField,
  Identifier,
  Labeled,
  Link,
  List,
  maxLength,
  ReferenceField,
  ReferenceInput,
  ReferenceManyField,
  regex,
  required,
  SelectInput,
  Show,
  ShowBase,
  ShowButton,
  SimpleForm,
  SimpleShowLayout,
  SingleFieldList,
  TextField,
  TextInput,
  Toolbar,
  useCreateSuggestionContext,
  useEditController,
  useGetIdentity,
  useListContext,
  useNotify,
  usePermissions,
  useRecordContext,
  useRedirect,
  useRefresh,
  useShowContext,
  useTranslate,
  useUnselectAll,
} from 'react-admin'
import {useFormContext} from 'react-hook-form'

import BulkDeleteButton from '../components/BulkDeleteButton'
import {
  ChatMessageTemplateFormPreview,
  ChatMessageTemplatePreview,
} from '../components/chatMessageTemplate/ChatMessageTemplateEditor'
import ConfirmationDialog from '../components/ConfirmationDialog'
import ExtendedDateTimeInput from '../components/ExtendedDateTimeInput'
import {
  AddIcon,
  ArrowRightIcon,
  ChevronRightIcon,
  ClockIcon,
  DownloadIcon,
  SearchIcon,
} from '../components/icons'
import RichTranslationText from '../components/RichTranslationText'
import SubtitledInput from '../components/SubtitledInput'
import ApiUrlsContext from '../contexts/ApiUrlsContext'
import {SandboxContext} from '../contexts/SandboxContext'
import useFormatTimestamp from '../hooks/dates/useFormatTimestamp'
import useHasPermission from '../hooks/useHasPermission'
import useIntervalRefresh from '../hooks/useIntervalRefresh'
import useIsDesktop from '../hooks/useIsDesktop'
import useIsOwner from '../hooks/useIsOwner'
import useQueryParameters from '../hooks/useQueryParameters'
import useSoftDelete from '../hooks/useSoftDelete'
import {
  Campaigns,
  ChatMessageTemplateButtonTypesEnum,
  ChatMessageTemplates,
  MerchantCustomerUsers,
  MutationRoot,
  QueryRoot,
} from '../types/graphqlSchema'
import {isTimestampInFuture, isValidDateTimeString} from '../utils/dates'
import DeleteWithConfirmButton from './components/DeleteWithConfirmButton'
import ExtendedList, {CustomFilterConfig} from './components/ExtendedList'
import ExtendedPagination from './components/ExtendedPagination'
import {CreateHeader, ShowHeader} from './components/ResourceHeader'


const CampaignCreate = () => {
  const [insertCampaign] = useMutation(
    CAMPAIGN_INSERT_MUTATION,
    {
      onCompleted: () => {
        unselectCustomerUsers()
        notify('resources.campaigns.create.success')
        redirect(`/campaigns`)
      },
      onError: e => {
        console.error(e)
        notify('resources.campaigns.create.error', {type: 'error'})
      },
    },
  )
  const redirect = useRedirect()
  const notify = useNotify()
  const styles = useStyles()
  const translate = useTranslate()
  const isOwner = useIsOwner()
  const [hasDisplayedEmptyFilters, setHasDisplayedEmptyFilters] = useState(false)
  const {permissions: {channelAdminChannelIds} = {}} = usePermissions<Permissions>()
  const unselectCustomerUsers = useUnselectAll('customer_users')
  const save = useCallback(async ({...formInputValues}) => {
    const formValues = pick(
      formInputValues, [
        'audienceType',
        'channelId',
        'chatMessageTemplateId',
        'name',
        'scheduledTimestamp',
        'templateButtonCouponCode',
        'templateButtonUrlParameters',
      ]
    )
    const campaignCustomerUsers = (formInputValues.campaignCustomerUserIds ?? []).map(
      customerUserId => ({customerUserId})
    )
    await insertCampaign({variables: {...formValues, campaignCustomerUsers}})
  }, [insertCampaign])
  return (
    <Create actions={<CreateHeader />} className={styles.create}>
      <SimpleForm
        defaultValues={{
          // TODO: Remove `audienceType` references in hasura, database, events-api and
          //    of the web-app since we now use the audience table to select customers.
          audienceType: 'SELECTED_CUSTOMER_USERS',
        }}
        toolbar={(
          <CreateToolbar
            hasDisplayedEmptyFilters={hasDisplayedEmptyFilters}
            save={save}
          />
        )}
      >
        <SubtitledInput
          subtitle={translate('resources.campaigns.create.inputs.name.subtitle')}
          title={translate('resources.campaigns.fields.name')}
        >
          <TextInput
            label={translate('resources.campaigns.fields.name')}
            source="name"
            validate={required()}
          />
        </SubtitledInput>
        <SubtitledInput
          subtitle={translate(
            'resources.campaigns.create.inputs.chatMessageTemplateId.subtitle'
          )}
          title={translate('resources.campaigns.fields.chatMessageTemplateId')}
        >
          <ReferenceInput
            createLabel={translate('resources.campaigns.createTemplate')}
            filter={{isDeleted: false, status: 'APPROVED', type: 'MARKETING'}}
            label={translate(
              'resources.campaigns.create.inputs.chatMessageTemplateId.label'
            )}
            perPage={false as any}
            reference="chat_message_templates"
            source="chatMessageTemplateId"
          >
            <AutocompleteInput
              filterToQuery={q => ({'name@_ilike': q})}
              createLabel={translate('resources.campaigns.createTemplateLabel')}
              create={<CreateTemplateDialog />}
              defaultValue={useQueryParameters().templateId}
              validate={required()}
            />
          </ReferenceInput>
        </SubtitledInput>
        <TemplateVariables />
        <ChatMessageTemplateFormPreview
          className={styles.chatMessageTemplateEditor}
          source="chatMessageTemplateId"
        />
        <SubtitledInput
          subtitle={translate('resources.campaigns.create.inputs.channelId.subtitle')}
          title={translate('resources.campaigns.fields.channelId')}
        >
          <ReferenceInput
            filter={!isOwner ? {'id': channelAdminChannelIds} : {}}
            label={translate('resources.campaigns.create.inputs.channelId.label')}
            // @ts-ignore -  Type 'false' is not assignable to type 'number | undefined'
            perPage={false}
            reference="channels"
            source="channelId"
          >
            <SelectInput
              optionValue="id"
              source="name"
              translateChoice={false}
              validate={required()}
            />
          </ReferenceInput>
        </SubtitledInput>
        <SubtitledInput
          subtitle={translate(
            'resources.campaigns.create.inputs.scheduledTimestamp.subtitle'
          )}
          title={translate('resources.campaigns.fields.scheduledTimestamp')}
        >
          {/* TODO: Internationalize the datetime input. */}
          <ExtendedDateTimeInput
            disablePast
            maxDateTime={addYears(new Date(), 3)}
            slotProps={{
              popper: {
                placement: 'top-end',
              },
            }}
            defaultValue={new Date()}
            label={translate(
              'resources.campaigns.create.inputs.scheduledTimestamp.label'
            )}
            parse={v => !isValidDateTimeString(v) ? undefined : new Date(v)}
            source="scheduledTimestamp"
            validate={[
              v => (v === undefined) || isValidDateTimeString(v) ?
                undefined :
                translate('validations.date'),
              v => (isToday(new Date(v)) || isTimestampInFuture(v)) ?
                undefined : translate('validations.date'),
            ]}
          />
        </SubtitledInput>
        <Typography gutterBottom variant="subtitle1">
          {translate('resources.campaigns.create.inputs.audienceType.subtitle')}
        </Typography>
        <MerchantCustomerUserList
          onHasDisplayedEmptyFiltersChange={setHasDisplayedEmptyFilters}
          source="campaignCustomerUserIds"
        />
      </SimpleForm>
    </Create>
  )
}

const TemplateVariables = () => {
  const {watch} = useFormContext()
  const {chatMessageTemplateId} = watch()
  if (!chatMessageTemplateId) return null
  return (
    <ShowBase id={chatMessageTemplateId} resource="chat_message_templates">
      <TemplateButtonParameterFields />
    </ShowBase>
  )
}

const TemplateButtonParameterFields = () => {
  const translate = useTranslate()
  const styles = useStyles()
  const templateRecord = useRecordContext<ChatMessageTemplates>()
  const urlButtons = templateRecord?.chatMessageTemplateButtons
    ?.filter(
      b => (b.type === ChatMessageTemplateButtonTypesEnum.Url) &&
        (b.urlType === 'DYNAMIC')
    ) ?? []
  const copyCodeButton = templateRecord?.chatMessageTemplateButtons
    ?.find(b => b.type === ChatMessageTemplateButtonTypesEnum.CopyCode)
  if (!(urlButtons.length || copyCodeButton)) return null
  return (
    <Card elevation={2} style={{marginBottom: '1rem', padding: '1.5rem', width: '95%'}}>
      <Typography variant="subtitle2" marginBottom="1.5rem">
        {translate('resources.campaigns.create.templateButtonParameterFields.title')}
      </Typography>
      <div>
        {urlButtons.map((b, i) => (
          <div key={b.id}>
            <Typography
              variant="subtitle2" marginBottom="1.5rem">
              {translate(
                'resources.campaigns.create.templateButtonParameterFields.url.title'
              )}
            </Typography>
            <Box display="flex" width="100%" gap={2}>
              <SubtitledInput
                title={translate(
                  'resources.campaigns.create.inputs.templateButtonParameterFields.url.' +
                  'text.title'
                )}
                subtitle=""
              >
                <TextInput source={b.urlText as string} style={{width: '100%'}} readOnly/>
              </SubtitledInput>
              <SubtitledInput
                title={
                  <RichTranslationText
                    className={styles.buttonUrlPreview}
                    translationKey={
                      'resources.campaigns.create.inputs.templateButtonParameterFields.' +
                      'url.url.title'
                    }
                    translationOptions={{buttonUrl: b.url}}
                  />
                }
                subtitle=""
              >
                <TextInput
                  label={translate(
                    'resources.campaigns.create.inputs.templateButtonParameterFields.' +
                    'url.url.label'
                  )}
                  source={`templateButtonUrlParameters.${i}`}
                  style={{width: '100%'}}
                  validate={required()}
                />
              </SubtitledInput>
            </Box>
          </div>
        ))}
      </div>
      {copyCodeButton && (
        <>
          <Divider />
          <br />
          <Box display="flex" width="100%" gap={2}>
            <SubtitledInput
              title={translate(
                'resources.campaigns.create.inputs.templateButtonParameterFields.' +
                'copyCode.title'
              )}
              subtitle=""
            >
              <TextInput
                // Must not translate
                source="Copy offer code"
                style={{width: '100%'}}
                readOnly
              />
            </SubtitledInput>
            <SubtitledInput
              title={translate(
                'resources.campaigns.create.inputs.templateButtonParameterFields.' +
                'copyCode.couponCode.title'
              )}
              subtitle=""
            >
              <TextInput
                label={copyCodeButton.copyCodeExample}
                source="templateButtonCouponCode"
                style={{width: '100%'}}
                validate={[
                  required(),
                  maxLength(15),
                  regex(/^[a-zA-Z0-9]+$/, translate('validations.alphanumeric')),
                ]}
              />
            </SubtitledInput>
          </Box>
        </>
      )}
    </Card>
  )
}

const MerchantCustomerUserList = ({onHasDisplayedEmptyFiltersChange, source}) => {
  const styles = useStyles()
  const translate = useTranslate()
  const {identity: {merchantId} = {}} = useGetIdentity()
  const {setValue, watch} = useFormContext()
  const audienceType = watch('audienceType')
  const onChange = useCallback(
    value => setValue(source, value),
    [setValue, source]
  )
  const {data: {tags = []} = {}} = useQuery<QueryRoot['tags']>(
    DISTINCT_TAGS_QUERY,
  )
  const advancedFiltersConfig = useMemo<CustomFilterConfig>(() => ({
    // TODO: disabling role, industry and city filters for now.
    'merchantCustomerUsers#companyCity': {
      input: ({label, source, ...props}) => (
        <TextInput label={label} source={source} {...props} />
      ),
      label: translate('resources.merchant_customer_users.fields.companyCity'),
      operators: [{value: '_eq'}, {value: '_neq'}, {value: '_ilike'}],
    },
    'merchantCustomerUsers#isMarketingConsentGranted': {
      input: ({label, source, ...props}) => (
        <SelectInput
          choices={[
            {name: translate('ra.boolean.true'), value: true},
            {name: translate('ra.boolean.false'), value: false},
          ]}
          className={styles.customerUserFilterMarketingConsentFilter}
          emptyText="ra.boolean.empty"
          label={label}
          optionText="name"
          optionValue="value"
          source={source}
          sx={{'& span[aria-hidden="true"]': {display: 'none'}}}
          {...props}
        />
      ),
      label: translate(
        'resources.merchant_customer_users.fields.isMarketingConsentGranted'
      ),
      operators: [{value: '_eq'}, {value: '_neq'}],
    },
    'merchantCustomerUsers#merchantCustomerUserTags#tagId': {
      input: ({source, ...props}) => (
        <AutocompleteInput
          choices={tags}
          key="merchantCustomerUsers#merchantCustomerUserTags#tagId"
          label={translate('resources.tags.fields.name')}
          optionText="name"
          optionValue="id"
          source={source}
          translateChoice={false}
          {...props}
        />
      ),
      label: translate('resources.tags.fields.name'),
      operators: [{value: '_eq'}, {value: '_neq'}],
    },
    'merchantCustomerUsers#primaryResponsibleMerchantUserId': {
      input: ({defaultValue, label, source, ...props}) => (
        <ReferenceInput
          filterToQuery={q => ({'firstName@_ilike,lastName@_ilike': q})}
          label={label}
          perPage={50}
          reference="merchant_users"
          sort={{field: 'firstName', order: 'ASC'}}
          source={source}
          {...props}
        >
          <AutocompleteInput
            label={translate(
              'resources.merchant_customer_users.fields' +
              '.primaryResponsibleMerchantUserId'
            )}
            optionText={({firstName, lastName}) => `${firstName} ${lastName}`}
            defaultValue={defaultValue}
          />
        </ReferenceInput>
      ),
      label: translate(
        'resources.merchant_customer_users.fields.primaryResponsibleMerchantUserId'
      ),
      operators: [{value: '_eq'}, {value: '_neq'}],
    },
    'merchantCustomerUsers#role': {
      input: ({label, source, ...props}) => (
        <TextInput label={label} source={source} {...props} />
      ),
      label: translate('resources.merchant_customer_users.fields.role'),
      operators: [{value: '_eq'}, {value: '_neq'}, {value: '_ilike'}],
    },
    'merchantCustomerUsers#trade': {
      input: ({label, source, ...props}) => (
        <TextInput label={label} source={source} {...props} />
      ),
      label: translate('resources.merchant_customer_users.fields.trade'),
      operators: [{value: '_eq'}, {value: '_neq'}, {value: '_ilike'}],
    },
  }), [styles.customerUserFilterMarketingConsentFilter, tags, translate])
  if (audienceType !== 'SELECTED_CUSTOMER_USERS') return null
  return (
    <Grid className={styles.customerUserListRoot} container item>
      <ExtendedList
        advancedFiltersConfig={advancedFiltersConfig}
        actions={false}
        canSelectAllPages
        filter={{'merchantCustomerUsers#merchantId': merchantId}}
        filters={[
          <FilterLiveSearch
            // @ts-ignore: 'InputProps' does not exist
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon color="secondary" />
                </InputAdornment>
              ),
              onKeyDown: e => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  e.stopPropagation()
                }
              },
            }}
            alwaysOn
            key="fullText"
            source={"merchantCustomerUsers#fullText@_ilike"}
          />,
          <AutocompleteInput
            choices={tags}
            key="merchantCustomerUsers#merchantCustomerUserTags#tagId"
            label={translate('resources.tags.fields.name')}
            optionText="name"
            optionValue="id"
            source="merchantCustomerUsers#merchantCustomerUserTags#tagId"
            translateChoice={false}
          />,
          <SelectInput
            choices={[
              {name: translate('ra.boolean.true'), value: 'true'},
              {name: translate('ra.boolean.false'), value: 'false'},
            ]}
            className={styles.customerUserFilterMarketingConsentFilter}
            isRequired
            key="merchantCustomerUsers#isMarketingConsentGranted"
            label={translate(
              'resources.merchant_customer_users.fields.isMarketingConsentGranted'
            )}
            optionText="name"
            optionValue="value"
            source="merchantCustomerUsers#isMarketingConsentGranted"
            sx={{'& span[aria-hidden="true"]': {display: 'none'}}}
          />,
          <ReferenceInput
            filterToQuery={q => ({'firstName@_ilike,lastName@_ilike': q})}
            key="merchantCustomerUsers#primaryResponsibleMerchantUserId"
            label={translate(
              'resources.merchant_customer_users.fields' +
              '.primaryResponsibleMerchantUserId'
            )}
            perPage={50}
            reference="merchant_users"
            sort={{field: 'firstName', order: 'ASC'}}
            source="merchantCustomerUsers#primaryResponsibleMerchantUserId"
          >
            <AutocompleteInput
              label={translate(
                'resources.merchant_customer_users.fields' +
                '.primaryResponsibleMerchantUserId'
              )}
              optionText={({firstName, lastName}) => `${firstName} ${lastName}`}
            />
          </ReferenceInput>,
        ]}
        onHasDisplayedEmptyFiltersChange={onHasDisplayedEmptyFiltersChange}
        pagination={
          <ExtendedPagination className={styles.pagination} rowsPerPageOptions={[]} />
        }
        resource="customer_users"
        sort={{
          field: [
            'merchantCustomerUsers_aggregate.min.firstName',
            'merchantCustomerUsers_aggregate.min.lastName',
          ].join(','),
          order: 'ASC',
        }}
      >
        <MerchantCustomerUserDatagrid hasBulkActionsButton onChange={onChange} />
      </ExtendedList>
    </Grid>
  )
}

const MerchantCustomerUserDatagrid: FC<MerchantCustomerUserDatagridProps> = ({
  hasBulkActionsButton = false, onChange,
}) => {
  const {selectedIds} = useListContext()
  const translate = useTranslate()
  useEffect(() => onChange?.(selectedIds), [selectedIds, onChange])
  return (
    <Datagrid bulkActionButtons={hasBulkActionsButton && <></>} optimized>
      <ReferenceManyField
        label={useTranslate()('resources.merchant_customer_users.fields.name')}
        reference="merchant_customer_users"
        sortBy={[
          'merchantCustomerUsers_aggregate.min.firstName',
          'merchantCustomerUsers_aggregate.min.lastName',
        ].join(',')}
        target="customerUserId"
      >
        <SingleFieldList>
          <FunctionField<MerchantCustomerUsers>
            render={u => [u?.firstName, u?.lastName].join(' ')}
          />
        </SingleFieldList>
      </ReferenceManyField>
      <TextField
        label={useTranslate()(
          'resources.merchant_customer_users.fields.whatsappPhoneNumber'
        )}
        source="whatsappPhoneNumber"
      />
      <ReferenceManyField
        label={useTranslate()(
          'resources.merchant_customer_users.fields.isMarketingConsentGranted'
        )}
        reference="merchant_customer_users"
        sortBy="merchantCustomerUsers_aggregate.min.isMarketingConsentGranted"
        target="customerUserId"
      >
        <SingleFieldList>
          <BooleanField looseValue source="isMarketingConsentGranted"/>
        </SingleFieldList>
      </ReferenceManyField>
      {useIsDesktop() && (
        <ReferenceManyField
          label={translate(
            'resources.merchant_customer_users.fields.companyName'
          )}
          reference="merchant_customer_users"
          sortBy="merchantCustomerUsers_aggregate.min.companyName"
          target="customerUserId"
        >
          <SingleFieldList>
            <TextField source="companyName"/>
          </SingleFieldList>
        </ReferenceManyField>
      )}
      <ReferenceManyField
        label={useTranslate()(
          'resources.merchant_customer_users.fields.emailAddress'
        )}
        reference="merchant_customer_users"
        sortBy="merchantCustomerUsers_aggregate.min.emailAddress"
        target="customerUserId"
      >
        <SingleFieldList>
          <TextField source="emailAddress"/>
        </SingleFieldList>
      </ReferenceManyField>
    </Datagrid>
  )
}

const CreateTemplateDialog = () => {
  // TODO: Instead of loosing all form input, store the current form state into the
  //       current route state. Then when coming back here, automatically pick the
  //       new template, if even if not yet approved. In the future, we want users to
  //       create campaigns with yet pending templates so that the campaign can be sent
  //       once the template has been approved.
  const redirect = useRedirect()
  const {onCancel} = useCreateSuggestionContext()
  return (
    <ConfirmationDialog
      confirmationText={useTranslate()(
        'resources.campaigns.createTemplateDialog.confirmationText'
      )}
      onCancel={onCancel}
      onConfirm={() => redirect('create', '/chat_message_templates')}
      open
      title={useTranslate()('resources.campaigns.createTemplate')}
    >
      <Typography paragraph variant="body1">
        {useTranslate()('resources.campaigns.createTemplateDialog.content')}
      </Typography>
    </ConfirmationDialog>
  )
}

const CreateToolbar = ({hasDisplayedEmptyFilters, save}) => {
  const [isOpen, setIsOpen] = useState(false)
  const {formState: {isSubmitting, isValid}, handleSubmit, watch} = useFormContext()
  const {audienceType, campaignCustomerUserIds, name, scheduledTimestamp} = watch()
  const customerUserFilter = {
    ALL: {},
    ALL_WITH_MARKETING_OPT_IN: {'merchantCustomerUsers#isMarketingConsentGranted': true},
    SELECTED_CUSTOMER_USERS: {id: campaignCustomerUserIds},
  }[audienceType]
  const formatTimestamp = useFormatTimestamp()
  const translate = useTranslate()
  if (!save) return null
  return (
    <Toolbar>
      <Button
        color="primary"
        disabled={
          hasDisplayedEmptyFilters ||
          !(isValid && campaignCustomerUserIds?.length)
        }
        onClick={() => setIsOpen(true)}
        variant="contained"
      >
        {translate('resources.campaigns.create.createAction')}
      </Button>
      <ConfirmationDialog
        confirmationText={translate('actions.send')}
        onCancel={() => setIsOpen(false)}
        onConfirm={event => {
          if (isSubmitting) return
          handleSubmit(save)(event)
          setIsOpen(isValid)
        }}
        open={isOpen}
        title={translate('resources.campaigns.create.toolbar.confirmDialog.title')}
      >
        {scheduledTimestamp ? (
          <Typography paragraph variant="body1">
            <RichTranslationText
              rootElement="span"
              translationKey={"resources.campaigns.create.toolbar.confirmDialog." +
                "scheduledCampaignMessage"
              }
              translationOptions={{
                name: name ?? '-',
                scheduledDatetime: formatTimestamp(
                  scheduledTimestamp?.toISOString(),
                ),
              }}
            />
          </Typography>
        ) : (
          <RichTranslationText
            rootElement="span"
            translationKey={"resources.campaigns.create.toolbar.confirmDialog." +
              "instantCampaignMessage"
            }
            translationOptions={{
              name: name ?? '-',
            }}
          />
        )}
        <List
          actions={false}
          empty={
            <Typography color="error">{translate(
              'resources.campaigns.create.toolbar.confirmDialog.emptyContacts'
            )}</Typography>
          }
          filter={customerUserFilter}
          resource="customer_users"
          sort={{
            field: [
              'merchantCustomerUsers_aggregate.min.firstName',
              'merchantCustomerUsers_aggregate.min.lastName',
            ].join(','),
            order: 'ASC',
          }}
          pagination={<ExtendedPagination rowsPerPageOptions={[]} />}
        >
          <MerchantCustomerUserDatagrid />
        </List>
        <br/>
        <Typography variant="body1">
          {translate(
            'resources.campaigns.create.toolbar.confirmDialog.termsAndConditions'
          )}
        </Typography>
      </ConfirmationDialog>
    </Toolbar>
  )
}

const CampaignShow = () => (
  <Show actions={<CampaignsShowHeader />}>
    <CampaignsSimpleShowLayout />
  </Show>
)

const CampaignsSimpleShowLayout = () => {
  const translate = useTranslate()
  return (
    <SimpleShowLayout>
      <Box>
        <Box display="flex" justifyContent="space-between">
          <div>
            <Typography variant="body1">
              {translate('resources.campaigns.show.campaignPerformanceTitle')}
            </Typography>
            <Typography color="textSecondary" variant="caption">
              {translate('resources.campaigns.show.campaignPerformanceSubtitle')}
            </Typography>
          </div>
          <CampaignMessagesMetadataExportButton />
        </Box>
        <CampaignMessagesCount />
      </Box>
      <CampaignTimestampField />
      <FunctionField<Campaigns>
        label={translate('resources.campaigns.fields.insertionUserId')}
        render={
          ({
            insertionUser: {merchantUser: {firstName, lastName} = {} as Campaigns} = {},
          }: any) => (
            <Typography variant="body1">{firstName} {lastName}</Typography>
          )}
      />
      <ReferenceField
        label={translate('resources.campaigns.fields.channelId')}
        link={false}
        reference="channels"
        source="channelId"
      >
        <TextField source="name" variant="body1"/>
      </ReferenceField>
      <ReferenceField
        label={translate('resources.campaigns.fields.chatMessageTemplateId')}
        link="show"
        reference="chat_message_templates"
        source="chatMessageTemplateId"
      >
        <TextField source="name" variant="body1" />
      </ReferenceField>
      <FunctionField<Campaigns>
        label={translate('resources.campaigns.show.templatePreview.label')}
        render={({chatMessageTemplateId} = {} as Campaigns) => (
          <ChatMessageTemplatePreview chatMessageTemplateId={chatMessageTemplateId} />
        )}
      />
      <ShowToolbar />
    </SimpleShowLayout>
  )
}

const CampaignMessagesCount = () => {
  const {id, isDeleted, status: previousStatus} = useRecordContext()
  const styles = useStyles()
  const refresh = useRefresh()
  const translate = useTranslate()
  const {
    data: {
      campaigns_by_pk: {
        deliveredMessageCount = 0,
        readMessageCount = 0,
        status = null,
        totalMessageCount = 1,
      } = {},
    } = {},
    networkStatus,
    refetch,
  } = useQuery<QueryRoot['campaigns_by_pk']>(
    CAMPAIGNS_MESSAGES_COUNT_QUERY,
    {notifyOnNetworkStatusChange: true, variables: {id}}
  )
  // Refetch only the message counts at an interval
  useEffect(() => {
    if (isDeleted || previousStatus === 'COMPLETED') return
    const intervalId = setInterval(() => refetch(), 60_000)
    return () => clearInterval(intervalId)
  }, [isDeleted, previousStatus, refetch])
  // Refresh entire view when the status changes; this is to make sure
  // other fields outside this component are only updated if status updates.
  useEffect(() => {
    if (!!status && status !== previousStatus) refresh()
  }, [previousStatus, refresh, status])
  return networkStatus === NetworkStatus.loading ? (
    <Box display="flex" flexDirection="column" gap=".5rem" paddingY="1rem">
      {[...Array(3).keys()]
        .map(i => <Skeleton height="1rem" key={i} variant="rectangular" />)}
    </Box>
  ) : (
    <Box className={styles.performance} display="flex" justifyContent="space-between">
      {[
        ['totalMessageCount', totalMessageCount],
        [
          'deliveredMessageCount',
          deliveredMessageCount,
          (deliveredMessageCount / totalMessageCount),
        ],
        [
          'readMessageCount',
          readMessageCount,
          (readMessageCount / deliveredMessageCount),
        ],
      ].map(([fieldName, value, rate]) => (
        <div key={fieldName}>
          <Typography variant="body1">
            {translate(`resources.campaigns.fields.${fieldName}`)}
          </Typography>
          <Typography variant="h2">{value}
          </Typography>
          {isNumber(rate) && (
            <Typography className={styles.performanceRate} variant="caption">
              <ArrowRightIcon size="inherit" />
              {isFinite(rate) ? round(rate * 100, 2) : 0}%
            </Typography>
          )}
        </div>
      ))}
    </Box>
  )
}

const CampaignTimestampField = () => {
  const translate = useTranslate()
  const record = useRecordContext<Campaigns>()
  const label = translate(`resources.campaigns.show.fields.${
    (
      record?.scheduledTimestamp &&
      (new Date(record.scheduledTimestamp) > new Date())
    ) ? 'scheduledFor' : 'sentOn'
  }`)
  return (
    <Labeled label={label}>
      <DateField
        showTime
        source={record?.scheduledTimestamp ? "scheduledTimestamp" : "insertionTimestamp"}
        variant="body1"
      />
    </Labeled>
  )
}

const ShowToolbar = () => {
  const {record: {id, name} = {}, resource} = useShowContext<any>()
  const editController = useEditController()
  const onConfirmDelete = useSoftDelete({ids: [id], resource})
  return (
    <Toolbar>
      <EditContextProvider value={editController}>
        <DeleteWithConfirmButton
          confirmContent={useTranslate()('resources.campaigns.show.confirmDelete')}
          confirmTitle={name}
          onConfirmDelete={() => onConfirmDelete()}
          resource={resource}
        />
      </EditContextProvider>
    </Toolbar>
  )
}

const CampaignsShowHeader = () => (
  <ShowHeader title={useRecordContext()?.name} />
)

const CampaignMessagesMetadataExportButton = () => {
  const campaign = useRecordContext()
  const {getFilesApiUrl} = useContext(ApiUrlsContext)
  const translate = useTranslate()
  const [
    exportCampaignMessageMetadata, {loading: isLoading},
  ] = useMutation<MutationRoot['exportCampaignMessagesMetadata']>(
    EXPORT_CAMPAIGN_MESSAGES_METADATA,
    {
      onCompleted({exportCampaignMessagesMetadata: {filename, key}}: any) {
        window.open(getFilesApiUrl(`${key}/${filename}`), '_self')
      },
      variables: {campaignId: campaign?.id},
    }
  )
  return (
    <Tooltip
      title={
        campaign.status !== 'COMPLETED' ? translate(
          'resources.campaigns.show.exportMessageMetadata.errors.' +
          `${{PENDING: 'PENDING_CAMPAIGN', SENDING: 'ONGOING_CAMPAIGN'}[campaign.status]}`
        ) : ''
      }
    >
      <div>
        <Button
          disabled={isLoading || campaign.status !== 'COMPLETED'}
          onClick={() => exportCampaignMessageMetadata()}
          startIcon={
            <DownloadIcon style={{visibility: isLoading ? 'hidden' : 'visible'}} />
          }
          variant="outlined"
        >
          <Box visibility={isLoading ? 'hidden' : 'visible'}>
            {translate('ra.action.export')}
          </Box>
          {isLoading && (
            <Box position="absolute"><CircularProgress size="18px" /></Box>
          )}
        </Button>
      </div>
    </Tooltip>
  )
}

const CampaignsList = () => {
  const {isSandbox} = useContext(SandboxContext)
  const isDesktop = useIsDesktop()
  useIntervalRefresh(60000)
  const translate = useTranslate()
  const {
    data: {merchant_users: merchantUsers = []} = {},
  } = useQuery<QueryRoot['merchant_users']>(
    MERCHANT_USERS_QUERY, {fetchPolicy: 'cache-first'},
  )
  return (
    <ExtendedList
      actionButtons={
        isDesktop && !isSandbox && (
          <Link to="/chat_message_templates/create">
            <Button startIcon={<AddIcon />} variant="outlined">
              {translate(`resources.chat_message_templates.action.create`)}
            </Button>
          </Link>
        )
      }
      description={
        <Typography color="textSecondary" paragraph>
          {useTranslate()('resources.campaigns.list.description')}
        </Typography>
      }
      disableSyncWithLocation={false}
      filter={{isDeleted: false, isListed: true}}
      filters={[
        <FilterLiveSearch
          // @ts-ignore: 'InputProps' does not exist
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon color="secondary" />
              </InputAdornment>
            ),
            onKeyDown: e => {
              if (e.key === 'Enter') {
                e.preventDefault()
                e.stopPropagation()
              }
            },
          }}
          alwaysOn
          key="name"
          source={"name@_ilike"}
        />,
        <ReferenceInput
          key="channelId"
          label={translate('resources.campaigns.fields.channelId')}
          perPage={100}
          reference="channels"
          sort={{field: 'name', order: 'ASC'}}
          source="channelId"
        >
          <SelectInput
            isRequired
            optionText="name"
            sx={{'& span[aria-hidden="true"]': {display: 'none'}}}
          />
        </ReferenceInput>,
        <ReferenceInput
          filter={{isDeleted: false, isListed: true}}
          key="chatMessageTemplateId"
          label={translate('resources.campaigns.fields.chatMessageTemplateId')}
          perPage={100}
          reference="chat_message_templates"
          sort={{field: 'name', order: 'ASC'}}
          source="chatMessageTemplateId"
        >
          <SelectInput
            isRequired
            optionText="name"
            sx={{'& span[aria-hidden="true"]': {display: 'none'}}}
          />
        </ReferenceInput>,
        <AutocompleteInput
          choices={merchantUsers.map(m => ({
            id: m.user?.id,
            name: `${m.firstName} ${m.lastName}`,
          }))}
          key="insertionUserId"
          label={translate('resources.campaigns.fields.insertionUserId')}
          optionText="name"
          optionValue="id"
          source="insertionUserId"
        />,
      ]}
      pagination={<ExtendedPagination rowsPerPageOptions={[]} />}
      perPage={25}
      sort={{field: 'insertionTimestamp', order: 'DESC'}}
    >
      <CampaignDatagrid />
    </ExtendedList>
  )
}

const CampaignDatagrid = () => {
  const styles = useStyles()
  const {isLoading} = useListContext()
  const isDesktop = useIsDesktop()
  const canDelete = useHasPermission('delete', 'campaigns')
  if (isLoading) return <LinearProgress />
  return (
    <Datagrid
      bulkActionButtons={canDelete && <BulkDeleteButton shouldSoftDelete />}
      className={styles.campaignsTable}
      rowClick={false}
    >
      <TextField
        headerClassName={styles.nameListColumnHeader}
        label="Name"
        source="name"
      />
      {isDesktop && (
        <ReferenceField
          headerClassName={styles.chatMessageTemplatesListColumnHeader}
          link={false}
          reference="chat_message_templates"
          source={"chatMessageTemplateId"}
        >
          <TextField source="name" />
        </ReferenceField>
      )}
      <FunctionField
        // @ts-ignore:
        //  Type instantiation is excessively deep and possibly infinite.
        //  This error appears here when adding `sortBy`.
        render={(r?: Campaigns) => {
          if (!r) return ''
          if (!r.scheduledTimestamp) {
            return new Date(r.insertionTimestamp).toLocaleString()
          }
          if (new Date().getTime() >= new Date(r.scheduledTimestamp).getTime()) {
            return new Date(r.scheduledTimestamp).toLocaleString()
          }
          return <ClockIcon color="secondary" />
        }}
        // This field must technically sort by the "sentTimestamp" which is either
        // the insertion timestamp or the scheduled timestamp. But there's no such field
        // in our campaigns table yet. So we sort by "insertionTimestamp" for convenience.
        // TODO: When a "sentTimestamp" column is added to the campaigns table, update
        // sortBy to use it here.
        sortBy="insertionTimestamp"
        source="sentTimestamp"
      />
      <ShowButton
        icon={<ChevronRightIcon color="action" />}
        label=""
        // @ts-ignore
        source="showIconButton"
      />
    </Datagrid>
  )
}

const DISTINCT_TAGS_QUERY = gql`query{
  tags{id name}
}`

const MERCHANT_USERS_QUERY = gql`query{
  merchant_users{firstName lastName user{id}}
}`

const CAMPAIGN_INSERT_MUTATION = gql`
  mutation (
    $audienceType: campaign_audience_types_enum!
    $campaignCustomerUsers: [campaign_customer_users_insert_input!]!
    $channelId: uuid!
    $chatMessageTemplateId: uuid!
    $name: String!
    $scheduledTimestamp: timestamptz
    $templateButtonCouponCode: String
    $templateButtonUrlParameters: [String!]
  ){
    insert_campaigns_one(
      object: {
        audienceType: $audienceType
        campaignCustomerUsers: {data: $campaignCustomerUsers}
        channelId: $channelId
        chatMessageTemplateId: $chatMessageTemplateId
        name: $name
        scheduledTimestamp: $scheduledTimestamp
        templateButtonCouponCode: $templateButtonCouponCode
        templateButtonUrlParameters: $templateButtonUrlParameters
      },
    ){id}
  }
`

const EXPORT_CAMPAIGN_MESSAGES_METADATA = gql`
  mutation($campaignId: uuid!){
    exportCampaignMessagesMetadata(campaignId: $campaignId){filename key}
  }
`

const CAMPAIGNS_MESSAGES_COUNT_QUERY = gql`
  query($id: uuid!) {
    campaigns_by_pk(id: $id){
      deliveredMessageCount
      readMessageCount
      status
      totalMessageCount
    }
  }
`

const useStyles = makeStyles(theme => ({
  buttonUrlPreview: {
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '30vw',
  },
  campaignsTable: {
    '& .RaDatagrid-table': {
      [theme.breakpoints.down('lg')]: {
        '& .MuiTableCell-paddingCheckbox:first-child': {
          paddingLeft: `${theme.remSpacing(1)} !important`,
          paddingRight: 0,
        },
        '& .column-name': {
          width: '40%',
        },
        '& .column-showIconButton': {
          width: theme.remSpacing(4),
        },
      },
    },
  },
  chatMessageTemplateEditor: {
    marginBottom: theme.remSpacing(3),
  },
  chatMessageTemplatesListColumnHeader: {
    width: '30%',
  },
  create: {
    [theme.breakpoints.up('lg')]: {
      maxWidth: 'unset', // overwrite theme's '50%' value
    },
  },
  createChatMessageTemplateLink: {
    color: theme.palette.info.main,
    cursor: 'pointer',
    fontWeight: 'bold',
    textDecoration: 'underline',
  },
  customerUserFilterMarketingConsentFilter: {
    marginBottom: 0,
  },
  customerUserListRoot: {
    [theme.breakpoints.up('lg')]: {
      '& .MuiFormControl-root': {
        width: '100%',
      },
      border: '1px solid',
      borderRadius: theme.remSpacing(0.75),
      width: '100%',
    },
    '& .MuiTableCell-paddingCheckbox': {
      padding: theme.remSpacing(1),
    },
    '& .MuiTableCell-root:first-of-type': {
      [theme.breakpoints.up('lg')]: {
        paddingLeft: `${theme.remSpacing(4)} !important`,
      },
    },
    padding: 0,
  },
  emptyCampaignList: {
    padding: 0,
  },
  emptyCustomerUserList: {
    '& .MuiTypography-root': {
      margin: 'auto',
    },
    height: '100%',
    padding: theme.remSpacing(3),
    width: theme.remSpacing(110),
  },
  nameListColumnHeader: {
    width: '20%',
  },
  pagination: {
    overflow: 'inherit',
  },
  performance: {
    '& $performanceRate': {
      color: theme.palette.info.main,
    },
    backgroundColor: theme.palette.background.paper,
    boxShadow: `0px 1px 3px ${theme.palette.background.secondary}`,
    marginTop: theme.remSpacing(1),
    padding: theme.remSpacing(2),
  },
  performanceRate: {},
}))

interface MerchantCustomerUserDatagridProps {
  hasBulkActionsButton?: boolean
  onChange?: (ids: Identifier[]) => void
}

export {
  CampaignCreate,
  CampaignShow,
  CampaignsList,
}
